import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LegalPage = ({ title, lastUpdated, children, testId }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div style={{ background: 'var(--sb-bg-3)', minHeight: '100vh', color: 'var(--sb-text-1)' }} data-testid={testId}>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ background: 'rgba(0,0,0,0.92)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
            <ArrowLeft size={16} />
            SwarmBase
          </Link>
          <span className="font-outfit font-black text-white text-sm">SWARM<span className="text-[#30d5c8]">BASE</span></span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-10">
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">Legal</p>
          <h1 className="font-outfit font-black text-white text-3xl md:text-4xl tracking-tight mb-3">{title}</h1>
          <p className="text-white/35 text-xs font-mono">Last Updated: {lastUpdated}</p>
        </div>
        <div className="flex flex-col gap-8">{children}</div>
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/25 text-xs font-mono">© 2026 SwarmBase. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Privacy</Link>
            <Link to="/terms" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Terms</Link>
            <Link to="/disclaimer" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Disclaimer</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

const Section = ({ number, title, children }) => (
  <section data-testid={`privacy-section-${number}`}>
    <h2 className="font-outfit font-bold text-white text-lg mb-3">{number}. {title}</h2>
    <div className="text-white/60 text-sm leading-relaxed flex flex-col gap-2">{children}</div>
  </section>
);

const Privacy = () => (
  <LegalPage title="Privacy Policy" lastUpdated="February 2026" testId="privacy-page">
    <Section number="1" title="Introduction">
      <p>SwarmBase operates the SwarmBase protocol, including the swarmbase.io website, the SwarmBase platform, and related services (collectively, the "Services"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Services. Please read this policy carefully. If you disagree with its terms, please discontinue use of our Services.</p>
    </Section>
    <Section number="2" title="Information We Collect">
      <p><strong className="text-white">Personal Information:</strong> When you register for the waitlist or create an account, we collect your name, email address, and wallet addresses (for Web3 interactions).</p>
      <p><strong className="text-white">Technical Information:</strong> We automatically collect IP address, browser type and version, operating system, device identifiers, pages visited, referral URLs, and timestamps of access.</p>
      <p><strong className="text-white">Blockchain Data:</strong> When you interact with our protocol, your wallet addresses and transaction hashes are recorded on public blockchains. This data is publicly accessible by design.</p>
      <p><strong className="text-white">Communications:</strong> If you contact us, we retain the content of your communications and your contact details.</p>
    </Section>
    <Section number="3" title="How We Use Your Information">
      <p>We use the information we collect to: (a) provide, operate, and maintain our Services; (b) improve, personalize, and expand our Services; (c) communicate with you, including for customer service and marketing; (d) process transactions and manage your account; (e) detect and prevent fraud and abuse; and (f) comply with legal obligations.</p>
    </Section>
    <Section number="4" title="Data Sharing">
      <p>We do NOT sell your personal data to third parties. We may share your information with:</p>
      <p><strong className="text-white">Service Providers:</strong> Third-party vendors who assist in operating our platform (hosting, analytics, email). These parties are bound by contractual obligations to protect your data.</p>
      <p><strong className="text-white">Legal Authorities:</strong> When required by law, court order, or to protect the rights and safety of our users or the public.</p>
      <p><strong className="text-white">Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets, where your data may be transferred as a business asset.</p>
    </Section>
    <Section number="5" title="Your Rights">
      <p>Under GDPR (EU/UK users), CCPA (California users), and applicable data protection laws, you have the right to: access the personal data we hold about you; correct inaccurate data; request deletion of your data; object to or restrict processing; and data portability. To exercise these rights, contact us at <strong className="text-[#30d5c8]">legal@swarmbase.io</strong>.</p>
    </Section>
    <Section number="6" title="Data Retention">
      <p>We retain personal data for as long as necessary to provide our Services and comply with legal obligations. Waitlist data is retained until you request deletion or the waitlist concludes. Technical logs are retained for 90 days.</p>
    </Section>
    <Section number="7" title="Security">
      <p>We implement industry-standard security measures including end-to-end encryption for data in transit (TLS 1.3), encryption at rest, access controls, regular security audits, and secure development practices. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.</p>
    </Section>
    <Section number="8" title="Cookies">
      <p>We use essential cookies necessary for the operation of our Services and analytics cookies (with your consent) to understand usage patterns. You can control cookie preferences through your browser settings. Disabling cookies may affect functionality.</p>
    </Section>
    <Section number="9" title="Contact">
      <p>For privacy-related inquiries, data requests, or to exercise your rights, contact our Data Protection team at <strong className="text-[#30d5c8]">legal@swarmbase.io</strong>.</p>
      <p>SwarmBase — Registered in the British Virgin Islands.</p>
    </Section>
  </LegalPage>
);

export default Privacy;
